import React from 'react';
import { api } from '../api.js';
import * as T from '../time.js';
import { PeoplePicker } from './people-picker.jsx';
import { LeaveAdmin } from './leave-admin.jsx';
import { LeaveCancelModal, leaveDates, PART_LABEL } from './leave-common.jsx';
import { AbsenceBoard } from './leave-absence.jsx';
import { dateTimeLabel, mapApprovals, mapLeave, mapPeople } from '../mapping.js';
import { openExternal, teamsChatUrl } from '../teams.js';
import { Avatar, Drawer, DrawerHead, Empty, Icon, KV, Lbl, Loading, Modal, PH, PageTabs, Panel, StatusPill } from './components.jsx';
import { applyServerData, DEPTS, ME, PEOPLE, POLICY } from './data.jsx';
import { AlertRow, Button, DataTable, Field, Metric, Pill, RadioGroup, SegmentedControl } from './ds/fgcp-ds.jsx';

// modules.jsx — 휴가 · 조직도 · 앱 (회의실·설비 예약은 booking.jsx, 게시판은 board.jsx, 업무는 task.jsx)
const MDT = DataTable;
const MBtn = Button;
const MPill = Pill;
const MField = Field;
const MMetric = Metric;
const MAlert = AlertRow;

const { useState: uM, useEffect: uM2 } = React;

const WD_KR = ['월', '화', '수', '목', '금', '토', '일'];
// ============================ 휴가관리 ============================
// 날짜는 앱 시간대 달력의 날(YYYY-MM-DD — time.js, 계획 문서 5.3 3d). 일수·시각·결재선·잔여·취소 판단은 서버가 한다(domain/leave) — 여기 계산은 미리보기다.
// 승인·반려는 전자결재 엔진(휴가신청서 결재 문서 — 받은 결재·대리결재·Teams 버튼과 같은 처리, 계획 문서 5.2 2-2a).
const ymdOf = (y, m0, d) => `${y}-${String(m0 + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const todayYmd = T.todayYmd;
const dowOf = T.dowOfYmd;
/** 쉬는 날 — 업무 정책의 쉬는 요일(ISO 1~7, 서버 /policy)·공휴일. 화면에 요일을 박지 않는다(계획 문서 5.4 4c) */
const offDayOf = (d, holidays) => POLICY.leave.daysOff.includes(T.dowOfYmd(d) || 7) || Boolean(holidays[d]);
/** 근무일 수 미리보기 — 서버 규칙(쉬는 요일·공휴일 제외)과 같다. 최종 일수는 서버 응답. holidays = { 'YYYY-MM-DD': 이름 } */
function workingDays(a, b, holidays) {
  let n = 0;
  for (let d = a; d <= b; d = T.addDays(d, 1)) {
    if (!offDayOf(d, holidays)) n += 1;
  }
  return n;
}
/** 그 해 공휴일 { 날짜: 이름 } — 관리자가 관리하는 표(서버). 달력 표시와 일수 미리보기에 쓴다 */
function useHolidays(year) {
  const [map, setMap] = uM({});
  uM2(() => {
    let current = true;
    api.get('/api/v1/leave/holidays?year=' + year)
      .then(r => { if (current) setMap(Object.fromEntries(r.holidays.map(h => [h.date, h.name]))); })
      .catch(() => { if (current) setMap({}); });   // 미리보기용 — 최종 일수·거부는 서버가 신청 때 정한다
    return () => { current = false; };
  }, [year]);
  return map;
}
/** 기간을 차지하는 상태(서버 겹침 제약과 같다 — 반려·취소는 빠진다) */
const LIVE = ['submitted', 'approved'];
/** 결재선 줄 종류 표시 — 서버 결재 줄·수신자 종류 */
const LINE_KIND_LABEL = { approve: '결재', agree: '합의', cc: '참조', view: '열람' };
function LeaveCalendar({ month, onMonth, sel, onDay, mine, holidays }) {
  const count = T.daysInMonth(month.y, month.m0 + 1);
  const lead = (T.dowOfYmd(ymdOf(month.y, month.m0, 1)) + 6) % 7;   // 월요일부터
  const today = todayYmd();
  const thisMonth = T.parts();
  const atThisMonth = month.y === thisMonth.y && month.m0 === thisMonth.m - 1;
  // 내 신청(진행 중·승인)이 걸친 날 — 겹치게 고르지 않도록 보여 준다(반려·취소는 빈 날)
  const taken = d => mine.find(r => LIVE.includes(r.status) && r.from <= d && d <= r.to);
  return (
    <div style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 10 }}>
        <button className="ico" aria-label="이전 달" disabled={atThisMonth} onClick={() => onMonth(-1)}><Icon name="chevron-left" size={14} /></button>
        <span className="mono" style={{ fontSize: 12.5, fontWeight: 700 }}>{month.y}년 {month.m0 + 1}월</span>
        <button className="ico" aria-label="다음 달" onClick={() => onMonth(1)}><Icon name="chevron-right" size={14} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5, marginBottom: 6 }}>
        {WD_KR.map((d, i) => <div key={d} className="mono" style={{ textAlign: 'center', fontSize: 9.5, fontWeight: 600, letterSpacing: '.06em', color: i === 6 ? 'var(--crit)' : i === 5 ? 'var(--info)' : 'var(--ink-3)' }}>{d}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5 }}>
        {Array.from({ length: lead }, (_, i) => <span key={'b' + i} />)}
        {Array.from({ length: count }, (_, i) => i + 1).map(n => {
          const d = ymdOf(month.y, month.m0, n);
          // 지난 날은 고르지 못한다 — 업무 정책이 지난 날짜 신청을 허용하면 고른다(최종 판단은 서버)
          const past = d < today && !POLICY.leave.allowPastDates, isToday = d === today, picked = sel && d >= sel.start && d <= sel.end;
          const isStart = sel && d === sel.start, isEnd = sel && d === sel.end;
          const t = taken(d);
          const holiday = holidays[d];
          return (
            <button key={d} className="dayc" data-date={d} data-holiday={holiday ? true : undefined} title={holiday} disabled={past} onClick={() => onDay(d)} style={{
              minHeight: 54, alignItems: 'flex-start', padding: 6, opacity: past ? .35 : 1, cursor: past ? 'not-allowed' : 'pointer',
              borderColor: picked ? 'var(--acc-graph)' : isToday ? 'var(--line-strong)' : 'var(--line)',
              background: picked ? 'var(--acc-tint)' : 'var(--surface-2)' }}>
              <span className="mono" style={{ fontSize: 11, fontWeight: 600, color: picked ? 'var(--acc)' : holiday ? 'var(--crit)' : POLICY.leave.daysOff.includes(dowOf(d) || 7) ? 'var(--ink-3)' : 'var(--ink-2)' }}>{n}</span>
              {holiday && !t && <span style={{ marginTop: 'auto', fontSize: 9, color: 'var(--crit)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{holiday}</span>}
              {(isStart || isEnd) && sel.start !== sel.end && <span className="mono" style={{ position: 'absolute', top: 5, right: 5, fontSize: 7.5, fontWeight: 700, color: 'var(--acc)' }}>{isStart ? 'IN' : 'OUT'}</span>}
              {isToday && !picked && <span className="mono" style={{ position: 'absolute', top: 5, right: 5, fontSize: 7.5, fontWeight: 700, color: 'var(--acc)' }}>TODAY</span>}
              {!picked && t && <span style={{ marginTop: 'auto', fontSize: 9.5, fontWeight: 600, color: t.status === 'approved' ? 'var(--ok)' : 'var(--warn)' }}>{t.type}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 신청 창. 결재선은 서버가 준 미리보기(휴가신청서 기본 결재선을 나에게 맞게 푼 것) — 첫 결재자만 바꿀 수 있고 나머지 줄은 그대로 간다.
 * 결재가 필요 없는 종류는 결재선 없이 바로 승인된다(서버 leave_types.approval_required).
 */
function LeaveModal({ range, types, line, onClose, onSubmit }) {
  // 기본 종류: 자기 잔여를 쓰는 종류(예: 연차) — 코드가 아니라 서버가 준 잔여 관계로 고른다
  const [typeId, setTypeId] = uM(() => (types.find(t => t.balance_type_id === t.id) ?? types[0])?.id ?? '');
  const [part, setPart] = uM('am');
  const [reason, setReason] = uM('');
  const [approverIds, setApproverIds] = uM([]);   // 비어 있으면 서버가 정한 기본 결재자
  const defaultApprover = line?.approver ?? null;
  const [changing, setChanging] = uM(!defaultApprover);
  const [balance, setBalance] = uM(null);
  const type = types.find(t => t.id === typeId);
  const half = type?.unit === 'half_day';
  const needsApproval = type?.approval_required !== false;
  const year = range.start.slice(0, 4);
  const holidays = useHolidays(year);
  // 신청 기간 연도의 잔여(그 종류가 빼는 잔여)
  uM2(() => {
    api.get('/api/v1/leave/balance?year=' + year).then(r => setBalance(r.balances)).catch(() => setBalance([]));
  }, [year]);
  const bal = type?.balance_type_id ? (balance ?? []).find(b => b.leaveTypeId === type.balance_type_id) : null;
  const days = half ? 0.5 : workingDays(range.start, range.end, holidays);
  const left = bal ? bal.remainingDays - bal.pendingDays : null;
  const problem = range.start.slice(0, 4) !== range.end.slice(0, 4) ? '연도가 바뀌는 기간은 연도별로 나눠 신청하세요'
    : half && range.start !== range.end ? '반일 휴가는 하루만 고르세요'
    : half && holidays[range.start] ? `공휴일(${holidays[range.start]})에는 휴가를 신청하지 않습니다`
    : days === 0 ? '고른 기간이 모두 쉬는 날입니다'
    : type?.balance_type_id && balance && !bal?.recorded ? `${year}년 잔여(발생) 기록이 없습니다 — 휴가 관리자에게 문의하세요`
    : left !== null && days > left ? `잔여가 부족합니다 (남은 ${left}일)`
    : needsApproval && !line ? '결재선을 불러오는 중입니다'
    : needsApproval && !defaultApprover && approverIds.length === 0 ? '결재자를 지정하세요' : '';
  const dateLabel = range.start === range.end ? range.start + (half ? ' ' + PART_LABEL[part] : '') : `${range.start} ~ ${range.end}`;
  const chosen = approverIds.length ? PEOPLE.find(p => p.id === approverIds[0]) : null;
  const others = (line?.others ?? []).filter(o => o.user.id !== approverIds[0]);   // 지정한 사람이 뒤 줄에도 있으면 서버가 뒤 줄을 뺀다
  return (
    <Modal width={450} title="휴가 신청" icon="umbrella" onClose={onClose}
      footer={<React.Fragment>
        <MBtn variant="secondary" size="sm" onClick={onClose}>닫기</MBtn>
        <MBtn variant="primary" size="sm" disabled={!type || Boolean(problem)} onClick={() => onSubmit({
          leaveTypeId: type.id, startDate: range.start, endDate: range.end, dayPart: half ? part : undefined,
          reason, approverId: needsApproval ? approverIds[0] : undefined
        })}>{needsApproval ? '신청 · 상신' : '신청'}</MBtn>
      </React.Fragment>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)', background: 'var(--surface-2)' }}>
          <Icon name="calendar" size={16} color="var(--acc)" />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="mono" style={{ fontSize: 13, fontWeight: 700 }}>{dateLabel}</div>
            <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
              {bal ? `${bal.name} 잔여 ${left}일 → ${left - days}일` : type?.balance_type_id ? '잔여 확인 중…' : '잔여를 따로 관리하지 않는 종류'}
            </div>
          </div>
          <MPill tone="accent" dot={false}><span className="mono">{days}일</span></MPill>
        </div>
        <div>
          <Lbl style={{ marginBottom: 8 }}>휴가 종류</Lbl>
          <RadioGroup orientation="horizontal" value={typeId} onChange={setTypeId} options={types.map(t => ({ value: t.id, label: t.name }))} aria-label="휴가 종류" />
        </div>
        {half && (
          <div>
            <Lbl style={{ marginBottom: 8 }}>오전 · 오후</Lbl>
            <SegmentedControl size="sm" value={part} onChange={setPart} options={['am', 'pm'].map(p => ({ value: p, label: PART_LABEL[p] }))} aria-label="오전 · 오후" />
          </div>
        )}
        <MField label="사유" as="textarea" value={reason} onChange={e => setReason(e.target.value)} placeholder="휴가 사유를 입력하세요" />
        {needsApproval ? (
          <div data-leave-line>
            <Lbl style={{ marginBottom: 8 }}>결재선 <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>— 휴가신청서 결재 문서로 올라갑니다</span></Lbl>
            {!changing ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                <Avatar name={defaultApprover.name} size={22} />
                <b>{defaultApprover.name}</b>
                <span style={{ color: 'var(--ink-3)' }}>{[defaultApprover.title, defaultApprover.department_name].filter(Boolean).join(' · ')}</span>
                <button className="link" style={{ marginLeft: 'auto' }} onClick={() => setChanging(true)}>바꾸기</button>
              </div>
            ) : (
              <React.Fragment>
                <PeoplePicker label={defaultApprover ? '다른 결재자' : '정해진 부서장이 없어 결재자를 지정합니다'} value={approverIds} onChange={setApproverIds} max={1} />
                {defaultApprover && approverIds.length === 0 && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>고르지 않으면 {defaultApprover.name}님에게 갑니다</div>}
                {chosen && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>{chosen.name}님에게 결재를 요청합니다</div>}
              </React.Fragment>
            )}
            {others.length > 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--ink-2)', marginTop: 8 }} data-leave-line-others>
                이어서 {others.map(o => `${o.user.name}(${LINE_KIND_LABEL[o.kind] ?? o.kind})`).join(' · ')}
              </div>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--ink-2)' }} data-leave-no-approval>결재 없이 바로 승인되는 종류입니다</div>
        )}
        {problem && <div style={{ fontSize: 11.5, color: 'var(--crit)' }}>{problem}</div>}
      </div>
    </Modal>
  );
}

/** 내 잔여 내역 — 잔여(발생·조정·사용·진행)와 장부(발생·조정 기록). 장부는 휴가 관리자가 바꾼 내역이다 */
function LedgerModal({ year: initialYear, onClose, onToast }) {
  const [year, setYear] = uM(initialYear);
  const [ledger, setLedger] = uM(null);
  uM2(() => {
    let current = true;
    api.get('/api/v1/leave/balances/ledger?year=' + year).then(r => { if (current) setLedger(r); }).catch(e => { if (current) onToast(e.message); });
    return () => { current = false; };
  }, [year, onToast]);
  return (
    <Modal width={560} title="잔여 내역" icon="umbrella" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-my-ledger>
        <select className="inp" style={{ width: 92 }} value={year} onChange={e => setYear(Number(e.target.value))} aria-label="잔여 연도">
          {[initialYear + 1, initialYear, initialYear - 1].map(y => <option key={y} value={y}>{y}년</option>)}
        </select>
        {!ledger ? <Loading title="불러오는 중" /> : (
          <React.Fragment>
            {ledger.balances.map(b => (
              <div key={b.leaveTypeId} style={{ fontSize: 12.5 }}>
                <b>{b.name}</b> 남은 {b.remainingDays}일 — 발생 {b.grantedDays} · 조정 {b.adjustedDays} · 사용 {b.usedDays} · 진행 {b.pendingDays}
              </div>
            ))}
            {ledger.entries.length === 0 ? <Empty title="발생·조정 기록이 없습니다" /> : (
              <MDT columns={[
                { key: 'kind', header: '구분', width: 52, render: e => (e.kind === 'grant' ? '발생' : '조정') },
                { key: 'type_name', header: '종류', width: 72 },
                { key: 'change', header: '변화', mono: true, align: 'right', width: 60, render: e => (e.change_days > 0 ? '+' : '') + e.change_days + '일' },
                { key: 'reason', header: '사유', render: e => <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{e.reason || '—'}</span> },
                { key: 'at', header: '날짜', mono: true, width: 92, render: e => T.ymd(e.created_at) },
              ]} rows={ledger.entries} />
            )}
          </React.Fragment>
        )}
      </div>
    </Modal>
  );
}

function Leave({ onToast, onChanged }) {
  const now = T.parts();   // 앱 시간대의 오늘(연·월)
  const [tab, setTab] = uM('leave');
  const [manage, setManage] = uM(false);
  const [month, setMonth] = uM({ y: now.y, m0: now.m - 1 });
  const [sel, setSel] = uM(null);
  const [modal, setModal] = uM(false);
  const [types, setTypes] = uM([]);
  const [line, setLine] = uM(null);
  const [cancelling, setCancelling] = uM(null);
  const [data, setData] = uM({ balances: [], mine: [], inbox: [] });
  const [historyYear, setHistoryYear] = uM(now.y);
  const [history, setHistory] = uM(null);
  const [ledgerOpen, setLedgerOpen] = uM(false);
  const [rev, setRev] = uM(0);
  const picking = sel && sel.start === sel.end && !modal;

  // 열 때와 처리 뒤 서버에서 다시 받는다. 홈이 쓰는 휴가 요약(LEAVE)도 같이 갱신한다.
  // 받은 휴가 신청 = 결재 엔진의 받은 결재(지금 내 차례 — 대리 포함) 중 휴가 대상
  const reload = React.useCallback(() => Promise.all([
    api.get('/api/v1/leave/balance'), api.get('/api/v1/leave/requests'),
    api.get('/api/v1/approvals?box=inbox&subject=leave_request&pageSize=100')
  ]).then(([b, m, i]) => {
    setData({ balances: b.balances, mine: m.requests, inbox: mapApprovals(i.documents) });
    applyServerData({ leave: mapLeave(b, m.requests) });
  }).catch(e => onToast(e.message)), [onToast]);
  uM2(() => {
    void reload();
    // 사용 중인 종류와 휴가 관리 권한(서버가 정한 actions.manage — '휴가 관리' 탭)
    api.get('/api/v1/leave/types').then(r => { setTypes(r.types); setManage(r.actions?.manage === true); }).catch(e => onToast(e.message));
    api.get('/api/v1/leave/line').then(setLine).catch(e => onToast(e.message));
  }, [reload, onToast]);
  // 휴가 이력 — 그 해 내 신청 전부(모든 상태·취소 사유). 연도를 바꾸거나 처리하면 서버에서 다시 받는다
  uM2(() => {
    let current = true;
    api.get('/api/v1/leave/requests?year=' + historyYear)
      .then(r => { if (current) setHistory(r.requests); })
      .catch(e => { if (current) onToast(e.message); });
    return () => { current = false; };
  }, [historyYear, rev, onToast]);
  // 처리 뒤: 이 화면 값과 결재 배지·홈(휴가 신청은 결재 문서와 함께 바뀐다)을 서버에서 다시 받는다
  const after = msg => { onToast(msg); onChanged?.(); setRev(x => x + 1); return reload(); };

  const onDay = d => {
    if (!sel || modal || sel.start !== sel.end) { setSel({ start: d, end: d }); return; }
    setSel({ start: d < sel.start ? d : sel.start, end: d < sel.start ? sel.start : d }); setModal(true);
  };
  const reset = () => { setSel(null); setModal(false); };
  const shiftMonth = k => setMonth(m => { const t = T.shiftMonth(m.y, m.m0 + 1, k); return { y: t.y, m0: t.m - 1 }; });

  const submit = p => {
    api.post('/api/v1/leave/requests', p)
      .then(r => { reset(); return after(r.status === 'approved'
        ? `휴가 신청 완료 · ${r.amountDays}일 — 결재 없이 승인됐습니다`
        : `휴가 신청 완료 · ${r.amountDays}일 — ${r.approver.name}님 결재 대기 (${r.docNo})`); })
      .catch(e => onToast(e.message));
  };
  // 승인·반려는 결재 API — 합의 줄이면 합의·반대(같은 처리, 줄 종류만 다르다)
  const decide = (d, action) => {
    const agree = d.currentKind === 'agree';
    api.post(`/api/v1/approvals/${d.id}/${action}`, {})
      .then(r => after(action === 'reject'
        ? (agree ? '반대했습니다 — 휴가 신청이 반려됐습니다' : '휴가 신청을 반려했습니다')
        : r.status === 'approved' ? '휴가를 최종 승인했습니다' : `${agree ? '합의' : '승인'}했습니다 — 다음 차례로 넘어갔습니다`))
      .catch(e => onToast(e.message));
  };
  const cancel = reason => api.post(`/api/v1/leave/requests/${cancelling.id}/cancel`, { reason })
    .then(() => { const was = cancelling.status; setCancelling(null); return after(was === 'submitted' ? '휴가 신청을 취소했습니다(결재 회수)' : '휴가를 취소했습니다'); })
    .catch(e => onToast(e.message));

  // 대표 잔여 = 서버가 그 해 총 일수(발생 + 조정)가 많은 순으로 준 첫 잔여(종류 코드로 고르지 않는다). 나머지는 머리글에 남은 일수로
  const [primary, ...otherBalances] = data.balances;
  const today = todayYmd();
  const ymdLocal = T.ymd;   // 앱 시간대의 날
  const mine = data.mine.map(r => ({ ...r, from: ymdLocal(r.starts_at), to: ymdLocal(r.ends_at), type: r.type_name }));
  const waiting = data.inbox.filter(d => d.myTurn);
  const upcoming = data.mine.filter(r => LIVE.includes(r.status) && ymdLocal(r.ends_at) >= today)
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  const holidays = useHolidays(month.y);
  return (
    <React.Fragment>
      <div className="head">
        <h1>휴가관리</h1>
        <div className="meta">
          {primary && <MPill tone="neutral" dot={false}><span className="mono">{primary.year} · {primary.name} 발생 {primary.grantedDays + primary.adjustedDays}일</span></MPill>}
          {otherBalances.map(b => <MPill key={b.leaveTypeId} tone="neutral" dot={false}><span className="mono" data-other-balance>{b.name} 남은 {b.remainingDays}일</span></MPill>)}
          {tab === 'leave' && <MPill tone="accent" dot>달력에서 날짜 선택</MPill>}
        </div>
        <div className="r"><MBtn variant="secondary" size="sm" onClick={() => setLedgerOpen(true)}>잔여 내역</MBtn></div>
      </div>
      <PageTabs value={tab} onChange={setTab} items={[{ value: 'leave', label: '휴가' }, { value: 'absence', label: '부재 현황' }, ...(manage ? [{ value: 'admin', label: '휴가 관리' }] : [])]} />
      {tab === 'admin' && manage ? <div className="body" style={{ paddingTop: 14 }}><LeaveAdmin onToast={onToast} /></div>
        : tab === 'absence' ? <div className="body" style={{ paddingTop: 14 }}><AbsenceBoard onToast={onToast} /></div> : (
      <div className="body">
        <div className="kpis">
          <MMetric label={primary ? '잔여 ' + primary.name : '잔여'} value={primary ? primary.remainingDays : 0} unit={'/ ' + (primary ? primary.grantedDays + primary.adjustedDays : 0) + '일'}
            delta={primary && primary.grantedDays + primary.adjustedDays ? '사용률 ' + Math.round(primary.usedDays / (primary.grantedDays + primary.adjustedDays) * 100) + '%' : undefined} />
          <MMetric label="사용" value={primary ? primary.usedDays : 0} unit="일" />
          <MMetric label="승인 대기" value={data.mine.filter(r => r.status === 'submitted').length} unit="건" tone="down" />
          <MMetric label="받은 신청" value={waiting.length} unit="건" />
        </div>
        <div className="row2">
          <Panel>
            <PH title="휴가 신청" sub={picking ? '끝나는 날을 클릭하세요(하루면 같은 날)' : '시작일을 클릭하세요'}
              right={picking ? <button className="link" onClick={() => setSel(null)}>선택 취소</button> : null} />
            <LeaveCalendar month={month} onMonth={shiftMonth} sel={sel} onDay={onDay} mine={mine} holidays={holidays} />
          </Panel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {waiting.length > 0 && (
              <Panel>
                <PH title="받은 휴가 신청" sub={waiting.length + '건 · 결재함과 같은 문서'} />
                {waiting.map(d => (
                  <div key={d.id} className="rowlist" data-leave-doc={d.id}>
                    <Avatar name={d.drafter} userId={d.drafterId} size={22} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {d.drafter} <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>{d.dept}</span>
                        {d.turnFor && <MPill tone="info" dot={false}>{d.turnFor} 대신</MPill>}
                      </span>
                      <a className="mono" href={'#/approval/' + d.id} style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-3)' }}>{d.title} · {d.docNo}</a>
                    </span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      <MBtn variant="primary" size="sm" onClick={() => decide(d, 'approve')}>{d.currentKind === 'agree' ? '합의' : '승인'}</MBtn>
                      <MBtn variant="danger" size="sm" onClick={() => decide(d, 'reject')}>{d.currentKind === 'agree' ? '반대' : '반려'}</MBtn>
                    </span>
                  </div>
                ))}
              </Panel>
            )}
            <Panel>
              <PH title="내 휴가 신청" sub={upcoming.length + '건 · 진행 중·예정'} />
              {upcoming.length === 0 && <Empty title="진행 중이거나 예정된 휴가가 없습니다" sub="달력에서 날짜를 골라 신청하세요" />}
              {upcoming.map(u => (
                <div key={u.id} className="rowlist" data-leave={u.id}>
                  <MPill tone="info" dot={false}>{u.type_name}</MPill>
                  <span className="mono" style={{ fontSize: 11.5, color: 'var(--ink-2)' }}>{leaveDates(u)} · {Number(u.amount_days)}일</span>
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                    {u.approval_document_id && <a className="link" href={'#/approval/' + u.approval_document_id} style={{ fontSize: 11.5 }}>결재 문서</a>}
                    <StatusPill s={u.status} />
                    {u.actions.cancel && <MBtn variant="secondary" size="sm" onClick={() => setCancelling(u)}>취소</MBtn>}
                  </span>
                </div>
              ))}
            </Panel>
            <Panel scroll>
              <PH title="휴가 이력" sub={history ? history.length + '건 · 모든 상태' : ''}
                right={<select className="inp" style={{ width: 92 }} value={historyYear} onChange={e => setHistoryYear(Number(e.target.value))} aria-label="이력 연도">
                  {[now.y + 1, now.y, now.y - 1, now.y - 2].map(y => <option key={y} value={y}>{y}년</option>)}
                </select>} />
              {!history ? <Loading title="불러오는 중" /> : history.length === 0 ? <Empty title={`${historyYear}년 휴가가 없습니다`} /> : (
                <MDT columns={[
                  { key: 'type_name', header: '종류', strong: true, width: 72 },
                  { key: 'date', header: '일자', mono: true, width: 96 },
                  { key: 'days', header: '일수', mono: true, align: 'right', width: 46 },
                  { key: 'status', header: '상태', width: 64, render: r => <StatusPill s={r.status} /> },
                  { key: 'note', header: '', render: r => <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }} data-history-note>
                    {r.status === 'cancelled' ? `${r.cancelled_by_name ?? ''} 취소${r.cancel_reason ? ' — ' + r.cancel_reason : ''}` : r.reason ?? ''}</span> },
                  { key: 'act', header: '', width: 56, render: r => (r.actions.cancel ? <MBtn variant="secondary" size="sm" onClick={() => setCancelling(r)}>취소</MBtn> : null) },
                ]} rows={history.map(h => ({ ...h, date: leaveDates(h), days: Number(h.amount_days) }))} />
              )}
            </Panel>
          </div>
        </div>
      </div>
      )}
      {modal && sel && <LeaveModal range={sel} types={types} line={line} onClose={reset} onSubmit={submit} />}
      {cancelling && <LeaveCancelModal req={cancelling} onClose={() => setCancelling(null)} onConfirm={cancel} />}
      {ledgerOpen && <LedgerModal year={now.y} onClose={() => setLedgerOpen(false)} onToast={onToast} />}
    </React.Fragment>
  );
}

// ============================ 조직도 ============================
// 조직도는 여러 단계다 — 하위 부서를 모두 모아 인원을 센다
const childrenOf = (id) => DEPTS.filter(d => d.parent === id);
const subtreeIds = (id) => [id, ...childrenOf(id).flatMap(c => subtreeIds(c.id))];
const depthOf = (d) => {
  let n = 0, cur = d;
  while (cur && cur.parent) { const p = DEPTS.find(x => x.id === cur.parent); if (!p || !p.parent) break; cur = p; n += 1; }
  return n;
};

function Org({ deepPerson }) {
  const root = DEPTS.find(d => d.parent === null);
  const first = DEPTS.find(d => d.parent === root?.id) ?? root;
  const [deptId, setDeptId] = uM(first?.id ?? null);
  const [person, setPerson] = uM(null);
  const [q, setQ] = uM('');
  const [found, setFound] = uM(null);   // 검색 결과(서버 — 이름·메일·부서·직급·사번)
  // 검색·링크로 들어온 사람은 바로 연다(서버 상세 — 목록에 없는 사람도 볼 수 있는 사람에게는 열린다)
  uM2(() => { if (deepPerson) setPerson({ id: deepPerson.id }); }, [deepPerson]);
  uM2(() => {
    const t = q.trim();
    if (!t) { setFound(null); return undefined; }
    let live = true;
    const h = setTimeout(() => {
      api.get('/api/v1/org/users?q=' + encodeURIComponent(t)).then(r => { if (live) setFound(mapPeople(r.users)); }).catch(() => { if (live) setFound([]); });
    }, 250);
    return () => { live = false; clearTimeout(h); };
  }, [q]);
  const dept = DEPTS.find(d => d.id === deptId) ?? first;
  if (!dept) return <Empty title="부서 정보가 없습니다" sub="관리자에게 문의하세요" />;

  // 선택한 부서와 그 하위 부서 인원을 함께 보여준다(사업부를 고르면 소속 팀·파트 인원까지) — 부서 id로 맞춘다(이름이 같은 부서가 있을 수 있다)
  const membersOf = (id) => { const ids = new Set(subtreeIds(id)); return PEOPLE.filter(p => ids.has(p.deptId)); };
  const people = found ?? membersOf(dept.id);
  const headcount = (id) => membersOf(id).length;
  const teamMail = 'mailto:' + people.map(p => p.email).filter(Boolean).join(',');
  // 루트 아래 전체를 깊이 순서대로 펼친다
  const flat = (id, acc = []) => { for (const c of childrenOf(id)) { acc.push(c); flat(c.id, acc); } return acc; };
  const tree = root ? flat(root.id) : [];
  return (
    <React.Fragment>
      <div className="head">
        <h1>조직도 · 사원</h1>
        <div className="meta" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="search" aria-label="사원 검색" placeholder="이름·메일·부서·직급·사번" value={q} onChange={e => setQ(e.target.value)} style={{
            fontFamily: 'var(--font-ui)', fontSize: 12.5, padding: '6px 10px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)',
            background: 'var(--surface)', color: 'var(--ink)', width: 220 }} />
          <MPill tone="neutral" dot={false}><span className="mono">재직 {PEOPLE.length}명</span></MPill>
        </div>
      </div>
      <div className="body">
        <div className="row2" style={{ gridTemplateColumns: '236px 1fr' }}>
          <Panel>
            <PH title="부서" sub={tree.length + '개'} />
            <div style={{ padding: 8 }}>
              <button onClick={() => { setDeptId(root?.id); setPerson(null); }} style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                padding: '7px 9px', fontSize: 12.5, fontWeight: 700,
                color: deptId === root?.id ? 'var(--acc)' : 'var(--ink)' }}>
                <Icon name="building" size={14} color="var(--ink-3)" />{root?.name ?? '회사'}
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--ink-3)' }}>{PEOPLE.length}</span>
              </button>
              {tree.map(d => {
                const on = d.id === deptId;
                return (
                  <button key={d.id} onClick={() => { setDeptId(d.id); setPerson(null); }} style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                    padding: '7px 9px', paddingLeft: 26 + depthOf(d) * 14, borderRadius: 'var(--r-sm)', fontSize: 12.5,
                    fontWeight: on ? 600 : 500, color: on ? 'var(--acc)' : 'var(--ink-2)',
                    background: on ? 'var(--acc-tint)' : 'transparent' }}>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</span>
                    <span className="mono" style={{ marginLeft: 'auto', fontSize: 10.5, color: on ? 'var(--acc)' : 'var(--ink-3)' }}>{headcount(d.id)}</span>
                  </button>
                );
              })}
            </div>
          </Panel>
          <Panel scroll>
            <PH title={found ? `'${q.trim()}' 검색` : dept.name} sub={found ? people.length + '명' : '코드 ' + dept.code + (dept.manager && dept.manager !== '-' ? ' · 부서장 ' + dept.manager : '') + ' · ' + people.length + '명'}
              right={people.length > 0 ? <MBtn variant="secondary" size="sm" href={teamMail}>팀 메일</MBtn> : null} />
            <MDT columns={[
              { key: 'name', header: '이름', strong: true, render: p => (
                <button onClick={() => setPerson(p)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600, color: 'var(--ink)' }}>
                  <Avatar name={p.name} userId={p.id} size={22} />{p.name}
                  <span style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 400 }}>{p.title}</span>
                </button>) },
              ...(found ? [{ key: 'dept', header: '부서', render: p => <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{p.dept}</span> }] : []),
              { key: 'emp', header: '사번', mono: true, width: 88, render: p => <span style={{ whiteSpace: 'nowrap' }}>{p.emp}</span> },
              { key: 'email', header: '이메일', render: p => <span style={{ fontSize: 12, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{p.email}</span> },
              { key: 'phone', header: '연락처', mono: true, width: 132, render: p => <span style={{ whiteSpace: 'nowrap' }}>{p.phone}</span> },
              { key: 'joined', header: '입사', mono: true, align: 'right', width: 92 },
              { key: 'status', header: '상태', width: 68, render: p => <WorkPill status={p.status} /> },
            ]} rows={people.map(p => ({ ...p, id: p.id }))} footer={<span>{people.length}명 · 클릭하면 상세</span>} />
          </Panel>
        </div>
      </div>
      {person && <PersonDrawer id={person.id} onClose={() => setPerson(null)} />}
    </React.Fragment>
  );
}

const WORK = { active: ['ok', '재직'], left: ['neutral', '퇴사'], suspended: ['warn', '사용 중지'] };
function WorkPill({ status }) {
  const [tone, label] = WORK[status] ?? WORK.suspended;
  return <MPill tone={tone} dot>{label}</MPill>;
}
const HISTORY_FIELD = { department: '부서', title: '직급', employment_type: '고용 형태', joined_at: '입사일', left_at: '퇴사일' };

/** 부서장 현황(그 사람 부서·상위 부서의 부서장에게만 서버가 준다) — 숫자·요약만 */
function TeamSummary({ s }) {
  const num = (label, n, warn) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>{label}</span>
      <span className="mono" style={{ fontSize: 15, fontWeight: 700, color: warn && n > 0 ? 'var(--crit)' : 'var(--ink)' }}>{n}</span>
    </div>);
  const row = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 };
  return (
    <div data-team-summary style={{ paddingTop: 14, borderTop: '1px solid var(--line)', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700 }}>부서장 현황</div>
      <div>
        <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 6 }}>휴가 ({s.leave.year}년)</div>
        {s.leave.balances.length === 0 ? <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>잔여 기록 없음</div> :
          s.leave.balances.map(b => <KV key={b.name} k={b.name} v={`잔여 ${b.remainingDays}일${b.pendingDays ? ` · 신청 중 ${b.pendingDays}일` : ''}`} />)}
        <div data-upcoming style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-2)' }}>
          {s.leave.upcoming.length === 0 ? '다가오는 휴가 없음' : s.leave.upcoming.map((l, n) => (
            <div key={n}>{l.start_date === l.end_date ? l.start_date : `${l.start_date} ~ ${l.end_date}`}{PART_LABEL[l.day_part] ? ' ' + PART_LABEL[l.day_part] : ''} · {l.type_name}</div>))}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 6 }}>결재</div>
        <div style={row}>{num('결재 차례', s.approvals.waiting)}{num('기안 진행', s.approvals.inProgress)}</div>
      </div>
      <div>
        <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 6 }}>업무(담당)</div>
        <div data-team-tasks style={row}>{num('할 일', s.tasks.todo)}{num('진행', s.tasks.doing)}{num('보류', s.tasks.hold)}{num('기한 지남', s.tasks.overdue, true)}</div>
      </div>
    </div>
  );
}

/** 사원 상세 — 서버가 보는 사람에 맞춰 준다(역할·고용 형태·퇴사일·변경 이력은 본인·관리자·부서장에게만 온다) */
function PersonDrawer({ id, onClose }) {
  const [u, setU] = uM(null);
  const [err, setErr] = uM('');
  uM2(() => {
    let live = true;
    setU(null); setErr('');
    api.get(`/api/v1/org/users/${id}`).then(r => { if (live) setU(r.user); }).catch(e => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [id]);
  // Teams 채팅은 Teams 링크(Teams 탭 안에서는 Teams가 연다), 메일은 메일 앱
  const chat = () => { void openExternal(teamsChatUrl([u.email])); };
  if (!u) return (
    <Drawer width={400} onClose={onClose}>
      <DrawerHead title="사원" onClose={onClose} />
      <div style={{ padding: 20 }}>{err ? <Empty icon="user-x" title="사원을 열 수 없습니다" sub={err} /> : <Loading title="불러오는 중" />}</div>
    </Drawer>
  );
  const phone = u.mobile_phone || u.business_phone || '-';
  return (
    <Drawer width={400} onClose={onClose}>
      <DrawerHead title={u.display_name + (u.title ? ' ' + u.title : '')} sub={u.department_name ?? ''} onClose={onClose} />
      <div data-person={u.id} style={{ padding: 20, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar name={u.display_name} userId={u.id} size={52} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-.02em' }}>{u.display_name}</div>
            <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>{u.employee_no ?? '-'} · {u.title ?? '-'}</div>
            <div data-work-status={u.status} style={{ marginTop: 6 }}><WorkPill status={u.status} /></div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <KV k="부서" v={u.department_name ?? '-'} mono={false} />
          <KV k="이메일" v={u.email} mono={false} />
          <KV k="연락처" v={phone} />
          <KV k="입사일" v={u.joined_at ?? '-'} />
          {u.actions.restricted && <>
            <KV k="고용 형태" v={u.employment_type ?? '-'} mono={false} />
            <KV k="퇴사일" v={u.left_at ?? '-'} />
            <KV k="역할" v={u.roles.map(r => r.name).join(', ') || '-'} mono={false} />
          </>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <MBtn variant="primary" size="sm" style={{ flex: 1 }} disabled={!u.email} onClick={chat}>Teams</MBtn>
          <MBtn variant="secondary" size="sm" style={{ flex: 1 }} disabled={!u.email} href={'mailto:' + u.email}>메일</MBtn>
        </div>
        {u.team_summary && <TeamSummary s={u.team_summary} />}
        {u.actions.restricted && (
          <div data-person-history style={{ paddingTop: 14, borderTop: '1px solid var(--line)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>변경 이력</div>
            {u.history.length === 0 ? <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>없음</div> :
              u.history.map((h, n) => (
                <div key={n} data-history={h.field} style={{ fontSize: 12, padding: '5px 0', borderBottom: '1px solid var(--line)' }}>
                  <b>{HISTORY_FIELD[h.field] ?? h.field}</b> {h.before_value ?? '없음'} → {h.after_value ?? '없음'}
                  <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>{dateTimeLabel(h.changed_at)}{h.changed_by_name ? ' · ' + h.changed_by_name : ''}</div>
                </div>))}
          </div>
        )}
        {u.actions.edit && <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>인사 기록은 관리자 화면의 조직도에서 바꿉니다.</div>}
      </div>
    </Drawer>
  );
}

export { Leave, LeaveCalendar, LeaveModal, Org, PersonDrawer, WD_KR };
